import React, { useEffect, useMemo } from 'react';
import ChatWidget from '../components/Chat/ChatWidget';

/**
 * Full-bleed chat surface served at /chat-embed.
 *
 * Loaded inside an iframe by /public/embed.js on external DH-owned sites
 * (community portal, ai.deliverhealth.com, etc.).  Renders ONLY the chat
 * panel — no Header / WhatsNewBanner / Footer.  The host page provides the
 * surrounding chrome (the floating button + visual position).
 *
 * Standalone-visit guard: if a user opens /chat-embed directly in a top-level
 * browser tab (not inside an iframe), we constrain the chat to a centered
 * 420×640 card on a soft DH-teal backdrop so it looks intentional instead of
 * a "broken fullscreen page".  Inside an iframe this guard is bypassed and
 * the chat fills the iframe edge-to-edge as designed.
 */
export default function ChatEmbedPage() {
  // Detect whether this page is loaded inside an iframe.  `window.top` access
  // throws on cross-origin embeds, which is itself proof we're iframed.
  const isInIframe = useMemo(() => {
    try { return window.self !== window.top; } catch (e) { return true; }
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  if (isInIframe) {
    // Iframe context — fill the iframe entirely (host controls outer size).
    return (
      <div style={{
        position: 'fixed', inset: 0,
        width: '100vw', height: '100vh',
        background: '#f8fafc',
        overflow: 'hidden',
      }}>
        <ChatWidget embedded />
      </div>
    );
  }

  // Standalone context — center a fixed-size chat card on a DH-themed backdrop.
  // Useful when previewing /chat-embed in a browser tab without an iframe.
  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, overflow: 'hidden',
      background:
        'radial-gradient(circle at 30% 20%, rgba(79,209,197,0.28) 0%, transparent 55%),' +
        'radial-gradient(circle at 70% 80%, rgba(29,156,156,0.32) 0%, transparent 55%),' +
        'linear-gradient(135deg, #0a1f33 0%, #0d4f52 50%, #061829 100%)',
    }}>
      <div style={{
        position: 'relative',
        width: 'min(420px, 100%)',
        height: 'min(640px, calc(100vh - 48px))',
        borderRadius: 18,
        boxShadow: '0 30px 80px rgba(0,0,0,0.40), 0 8px 24px rgba(13,79,82,0.30)',
        background: '#f8fafc',
        overflow: 'hidden',
      }}>
        <ChatWidget embedded />
      </div>
    </div>
  );
}
